import { Button, Modal, Textarea } from "@mantine/core";
import type React from "react";
import type { LocalPet, TransformedTrainer } from "../../../types/portal";

interface TrainingRequestModalProps {
  opened: boolean;
  trainer: TransformedTrainer | null;
  pets: LocalPet[];
  selectedPetId: string;
  requestDuration: number;
  requestExplanation: string;
  loading: boolean;
  onPetIdChange: (petId: string) => void;
  onDurationChange: (duration: number) => void;
  onExplanationChange: (text: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const MODAL_CLASS_NAMES = {
  content: "rounded-2xl overflow-hidden p-0",
  body: "p-0",
};

const TrainingRequestModal: React.FC<TrainingRequestModalProps> = ({
  opened,
  trainer,
  pets,
  selectedPetId,
  requestDuration,
  requestExplanation,
  loading,
  onPetIdChange,
  onDurationChange,
  onExplanationChange,
  onCancel,
  onConfirm,
}) => (
  <Modal
    opened={opened}
    onClose={onCancel}
    title=""
    withCloseButton={false}
    size="md"
    classNames={MODAL_CLASS_NAMES}
  >
    <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
      <h3 className="m-0 text-lg sm:text-xl font-semibold">Confirm Training Request</h3>
    </div>
    <div className="p-4 sm:p-6">
      <p className="text-[1.1rem] text-foreground mb-5 text-center leading-relaxed">
        Create an active session request with <strong>{trainer?.name}</strong>?
      </p>
      <div className="bg-muted rounded-lg p-4 mb-5">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[0.9rem] text-muted-foreground font-medium">Trainer:</span>
          <span className="text-[0.9rem] text-foreground font-semibold">{trainer?.name}</span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-[0.9rem] text-muted-foreground font-medium">Rate:</span>
          <span className="text-[0.9rem] text-foreground font-semibold">
            ${trainer?.baseRate} first 10 min + ${trainer?.perMinuteRate}/min after
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-[0.9rem] text-muted-foreground font-medium">Experience:</span>
          <span className="text-[0.9rem] text-foreground font-semibold">{trainer?.experience}</span>
        </div>
      </div>

      <div className="mb-5">
        <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
          Select Pet for Training:
          <span className="text-destructive"> *</span>
        </label>
        <select
          value={selectedPetId}
          onChange={(e) => onPetIdChange(e.target.value)}
          className="w-full py-2.5 px-3 border-2 border-border rounded-md text-[0.9rem] bg-card cursor-pointer transition-colors duration-200 outline-none"
          required
        >
          <option value="">Choose a pet...</option>
          {pets.map((pet) => (
            <option key={pet.id} value={pet.id}>
              {pet.pet_name || pet.name} {pet.race ? `(${pet.race})` : ""}{" "}
              {pet.age ? `- ${pet.age} years old` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-5">
        <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
          Session Duration (minutes):
          <span className="text-destructive"> *</span>
        </label>
        <select
          value={requestDuration}
          onChange={(e) => onDurationChange(parseInt(e.target.value, 10))}
          className="w-full py-2.5 px-3 border-2 border-border rounded-md text-[0.9rem] bg-card cursor-pointer transition-colors duration-200 outline-none"
        >
          <option value={10}>10 minutes</option>
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={45}>45 minutes</option>
          <option value={60}>60 minutes</option>
          <option value={90}>90 minutes</option>
        </select>
      </div>

      <div className="mb-5">
        <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
          Training Request Explanation:
          <span className="text-destructive"> *</span>
        </label>
        <Textarea
          value={requestExplanation}
          onChange={(e) => onExplanationChange(e.target.value)}
          placeholder="Please explain what you'd like to work on with your pet during this training session..."
          rows={4}
          required
        />
      </div>
    </div>
    <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
      <Button variant="default" className="min-w-[100px]" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        color="green"
        radius="md"
        className="min-w-[140px]"
        onClick={onConfirm}
        disabled={
          loading ||
          !selectedPetId ||
          requestExplanation.trim() === "" ||
          requestDuration <= 0
        }
      >
        {loading ? "Creating..." : "Yes, Create Request"}
      </Button>
    </div>
  </Modal>
);

export default TrainingRequestModal;
